/* App raiz — state global, modais, integração completa Fase 6A + Voice S2 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './hooks/useAuth';
import { useChat } from './hooks/useChat';
import { usePlantao } from './hooks/usePlantao';
import { useTrial } from './hooks/useTrial';
import { useTrialCredit } from './hooks/useTrialCredit';
import { useTranslations } from './hooks/useTranslations';
import { useVoicePairing } from './hooks/useVoicePairing';
import { LoginView } from './components/LoginView';
import { ChatView } from './components/ChatView';
import { Header } from './components/Header';
import { ProfileModal, clearStoredProfile } from './components/ProfileModal';
import { TemplatesModal, clearStoredTemplates } from './components/TemplatesModal';
import { PlantaoCloseModal } from './components/PlantaoCloseModal';
import VoicePairingModal from './components/VoicePairingModal';
import VoiceUpgradeModal, {
  CAKTO_URLS,
  type VoiceUpgradePlan,
} from './components/VoiceUpgradeModal';
import VoiceDisclaimerModal, {
  carregarVoiceDisclaimer,
} from './components/VoiceDisclaimerModal';
import { Analytics } from './components/Analytics';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { CookieBanner } from './components/CookieBanner';
import { LegalTermsGate } from './components/LegalTermsGate';
import { OnboardingTour } from './components/OnboardingTour';
import { useLegalConsent } from './hooks/useLegalConsent';
import {
  consumeGoogleAuthError,
  googleAuthErrorToKey,
} from './utils/googleAuthReturn';
import './styles/onboarding.css';
import type { Locale } from './types';

/** Estado dos 3 modais do voice — uma máquina única evita race entre fluxos */
type VoiceModalState = 'closed' | 'disclaimer' | 'pairing' | 'upgrade';

/**
 * Spinner de loading inicial — exibido enquanto verifica JWT no localStorage.
 * Estética CRT para manter coerência visual antes do app montar.
 */
function BootScreen() {
  return (
    <div
      aria-label="Carregando Dr. Pera IA"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--ink)',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '14px',
          fontWeight: 700,
          color: '#FFFFFF',
          textShadow: 'none',
          letterSpacing: '0.04em',
        }}
      >
        DR. PERA IA
      </div>
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '13px',
          color: 'var(--ink-mute)',
          display: 'flex',
          gap: '4px',
        }}
        aria-hidden="true"
      >
        <span style={{ animation: 'cursor-blink 0.6s step-end infinite' }}>●</span>
        <span style={{ animation: 'cursor-blink 0.6s step-end 0.2s infinite' }}>●</span>
        <span style={{ animation: 'cursor-blink 0.6s step-end 0.4s infinite' }}>●</span>
      </div>
    </div>
  );
}

export default function App() {
  const { user, jwt, loading: authLoading, requestMagicCode, login, logout } = useAuth();
  const { t, locale, setLocale, loadingI18n } = useTranslations();

  /* ── Trial v2 por dias (TRIAL_V2_API.md) — flag off = app igual a hoje ── */
  const trial = useTrial(!!user && !!jwt);

  /**
   * /login = caminho de re-entrada explícito (link "Já sou assinante?" do
   * paywall). Mesmo com trial v2 ligado, essa rota mostra a tela de login
   * atual em vez de entrar direto no chat anônimo.
   */
  const forceLoginPath =
    typeof window !== 'undefined' && window.location.pathname === '/login';

  /** true = visitante anônimo entra direto no chat (flag trial_v2 ligada) */
  const anonymousTrialMode = !user && !authLoading && trial.flagOn && !forceLoginPath;

  /* ── Login Google: erro do retorno (/auth/google#error=...) ──
     Consumido one-shot no mount (sessionStorage). Sucesso não passa por
     aqui — o JWT já foi salvo pelo main.tsx antes do App montar. */
  const [googleAuthErrorCode] = useState<string | null>(() =>
    consumeGoogleAuthError(),
  );

  /* ── State global de modais e features ── */
  const [soapMode, setSoapMode] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [plantaoCloseOpen, setPlantaoCloseOpen] = useState(false);
  /** Fluxo voice: closed → disclaimer (1ª vez) → pairing | upgrade (free/plantonista) */
  const [voiceModalState, setVoiceModalState] = useState<VoiceModalState>('closed');
  // Controle do onboarding — sincroniza fechamento do ProfileModal com o tour
  const [profileModalClosedForTour, setProfileModalClosedForTour] = useState(false);
  // Consent legal versionado (LGPD/GDPR)
  const legal = useLegalConsent();

  /* ── Hook de plantão 12h ── */
  const plantao = usePlantao();

  /* ── Hook de chat com mode + contexto plantão ── */
  const {
    messages,
    sessionId,
    loading: chatLoading,
    error: chatError,
    sendUserMessage,
    appendLocalMessage,
    clearSession,
    clearError,
  } = useChat({
    mode: soapMode ? 'prontuario' : 'chat',
    plantaoContext: plantao.ativo ? plantao.contexto : null,
    // Headers X-Anon-Id/X-Trial-Surface SÓ quando anônimo + flag ligada
    trialAnonId: anonymousTrialMode ? trial.anonId : null,
  });

  const { creditsInfo } = useTrialCredit(user);

  /* ── Login Google: em modo trial anônimo o erro do retorno vira mensagem
     local no chat (uma vez só). Fora do trial, o LoginView mostra o erro. ── */
  const googleErrorShownRef = useRef(false);
  useEffect(() => {
    if (googleAuthErrorCode === null || googleErrorShownRef.current) return;
    if (!anonymousTrialMode || loadingI18n) return;
    googleErrorShownRef.current = true;
    appendLocalMessage(t(googleAuthErrorToKey(googleAuthErrorCode)));
  }, [googleAuthErrorCode, anonymousTrialMode, loadingI18n, appendLocalMessage, t]);

  /**
   * Voice S2 — callback de transcrição.
   * Quando o celular termina de transcrever, manda o texto direto pro chat
   * como se o médico tivesse digitado e dado Ctrl+Enter.
   * (audioSeconds vem do hook mas não é usado aqui — métrica do backend.)
   */
  const handleVoiceTranscribed = useCallback(
    (text: string) => {
      void sendUserMessage(text);
    },
    [sendUserMessage],
  );

  // Trial v2 (02/07/2026): anônimo com flag ligada manda X-Anon-Id +
  // X-Trial-Surface: web no voice (pair-init/SSE/heartbeat) — mesmo padrão
  // do chat. Logado (JWT presente) segue Authorization Bearer: zero mudança.
  const voice = useVoicePairing(
    handleVoiceTranscribed,
    fetch,
    anonymousTrialMode ? trial.anonId : null,
  );

  /* ── Voice trial v2 (02/07/2026): roteia 403 do pair-init ──
     trial_ended    → fecha modal voice + reabre paywall trial (nonce) + refresh
     email_required → fecha modal voice + refresh (emailGate do ChatView liga
                      via trial.emailRequired do estado novo).
     cancelarPareamento limpa o erro/estado do voice — sem loop no effect. */
  const [trialPaywallReopenNonce, setTrialPaywallReopenNonce] = useState(0);
  const voiceError = voice.error;
  const cancelarPareamentoVoice = voice.cancelarPareamento;
  const trialFlagOn = trial.flagOn;
  const refreshTrial = trial.refresh;
  useEffect(() => {
    if (!trialFlagOn) return;
    if (voiceError !== 'trial_ended' && voiceError !== 'email_required') return;
    setVoiceModalState('closed');
    if (voiceError === 'trial_ended') {
      setTrialPaywallReopenNonce((n) => n + 1);
    }
    cancelarPareamentoVoice();
    void refreshTrial();
  }, [voiceError, cancelarPareamentoVoice, trialFlagOn, refreshTrial]);

  /**
   * UX 20/05/2026 — fecha modal do QR automaticamente quando celular pareou.
   * Médico não precisa mais ver o QR depois de escanear. VoiceButton no
   * Header continua com glow verde indicando sessão ativa. Estados de
   * gravação ('recording' → 'transcribing' → 'paired') ficam visíveis
   * implicitamente pelo chat (texto aparece quando transcrito).
   */
  useEffect(() => {
    if (
      voiceModalState === 'pairing' &&
      (voice.state === 'paired' ||
        voice.state === 'recording' ||
        voice.state === 'transcribing')
    ) {
      setVoiceModalState('closed');
    }
  }, [voice.state, voiceModalState]);

  /**
   * UX 20/05/2026 — cronômetro regressivo 12h no botão PLANTÃO ativo.
   * setInterval só roda quando plantão está ativo (cleanup automático).
   * Refresh 1s — impacto perf desprezível.
   */
  const [agoraMs, setAgoraMs] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!plantao.ativo) return;
    const id = window.setInterval(() => setAgoraMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [plantao.ativo]);

  /**
   * Formata o restante de plantão em HH:MM:SS (12h total). Quando ultrapassa,
   * retorna "00:00:00" — botão segue clicável pra médico fechar.
   */
  function formatPlantaoCountdown(inicio: number, now: number): string {
    const TOTAL_MS = 12 * 60 * 60 * 1000;
    const restanteMs = Math.max(0, TOTAL_MS - (now - inicio));
    const totalSec = Math.floor(restanteMs / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  const plantaoCountdown =
    plantao.ativo && typeof plantao.inicio === 'number'
      ? formatPlantaoCountdown(plantao.inicio, agoraMs)
      : null;

  /**
   * Click do VoiceButton no Header — state-aware (22/05/2026):
   *
   * Routing por estado da máquina voice:
   *   - paired / recording / transcribing → desconecta manualmente (LED verde → off)
   *   - disconnected → reconecta via heartbeat HEAD (LED vermelho → verde)
   *   - free/plantonista                  → upgrade modal (CTA Cakto)
   *   - idle/error/tier_blocked + 1ª vez  → disclaimer modal
   *   - idle/error/tier_blocked + aceito  → inicia pareamento direto
   *
   * NUNCA dispara pair-init quando já está em sessão — evita duplicar tokens.
   */
  const handleVoiceClick = useCallback(() => {
    // Sessão ativa (LED verde) → desconecta manualmente
    if (
      voice.state === 'paired' ||
      voice.state === 'recording' ||
      voice.state === 'transcribing'
    ) {
      voice.desconectar();
      return;
    }

    // Sessão caiu (LED vermelho) → tenta reconectar com lastToken
    if (voice.state === 'disconnected') {
      void voice.reconectar();
      return;
    }

    // Tier check — free/plantonista vê paywall.
    // Trial v2 (02/07/2026): com flag ligada + trial ativo, anônimo (tier
    // null) e logado free passam SEM gate client-side — o server decide
    // (200 libera voz; 403 trial_ended/email_required roteados no effect).
    // Plantonista e pagos: comportamento de hoje EXATO. Flag off → idem hoje.
    const tier = user?.tier ?? null;
    const trialVoiceBypass =
      trial.flagOn && trial.active && (tier === null || tier === 'free');
    if (
      !trialVoiceBypass &&
      (tier === null || tier === 'free' || tier === 'plantonista')
    ) {
      setVoiceModalState('upgrade');
      return;
    }

    // Fluxo normal — disclaimer (1ª vez) ou pair-init direto.
    // 23/05 noite: passa locale pra QR mobile abrir no idioma do médico.
    if (carregarVoiceDisclaimer()) {
      void voice.iniciarPareamento({ locale });
      setVoiceModalState('pairing');
    } else {
      setVoiceModalState('disclaimer');
    }
  }, [user, voice, locale, trial.flagOn, trial.active]);

  /** Aceite do disclaimer → segue pra pareamento (com locale do médico). */
  const handleVoiceDisclaimerAccept = useCallback(() => {
    setVoiceModalState('pairing');
    void voice.iniciarPareamento({ locale });
  }, [voice, locale]);

  /** Fecha o pairing modal mas mantém sessão SSE aberta pra reabrir depois */
  const handleVoicePairingClose = useCallback(() => {
    setVoiceModalState('closed');
  }, []);

  /** Cancela sessão SSE + fecha modal */
  const handleVoicePairingCancel = useCallback(() => {
    voice.cancelarPareamento();
    setVoiceModalState('closed');
  }, [voice]);

  /** Click num CTA Cakto do upgrade modal → abre checkout em nova aba */
  const handleVoiceUpgrade = useCallback((plan: VoiceUpgradePlan) => {
    window.open(CAKTO_URLS[plan], '_blank', 'noopener,noreferrer');
    setVoiceModalState('closed');
  }, []);

  /** voiceActive: tem sessão em curso (qualquer estado != idle/error) */
  const voiceActive =
    voice.state === 'waiting' ||
    voice.state === 'paired' ||
    voice.state === 'recording' ||
    voice.state === 'transcribing' ||
    voice.state === 'requesting';

  /* ── Atualiza lang do html quando idioma muda ── */
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  /* ── Handlers dos botões do Header ── */
  const handleToggleSoap = useCallback(() => {
    setSoapMode((prev) => !prev);
  }, []);

  const handleTogglePlantao = useCallback(() => {
    if (plantao.ativo) {
      // Clicou em "Fechar plantão" → abre modal de passagem
      setPlantaoCloseOpen(true);
    } else {
      // Inicia novo plantão
      plantao.iniciar();
    }
  }, [plantao]);

  /**
   * Confirmação do encerramento do plantão.
   * Fecha modal + limpa plantão + inicia nova sessão de chat
   * (para não misturar casos de plantões diferentes).
   */
  const handleConfirmFecharPlantao = useCallback(() => {
    setPlantaoCloseOpen(false);
    plantao.fechar();
    clearSession();
  }, [plantao, clearSession]);

  /**
   * Logout COMPLETO (fix 21/08/2026 — bug "salgo y no salgo" reportado pelo
   * Dr. Lu no dia da 1ª venda). O logout do useAuth só apaga o JWT; a
   * conversa (drpera-ia-chat-state-v1, TTL 12h), o perfil local e os
   * templates ficavam no navegador — numa PC compartilhada de UPA/hospital
   * o próximo médico via a conversa clínica do anterior.
   *
   * Limpa: chat (tela + localStorage + rotaciona sessionId), perfil local,
   * templates locais, e por fim o JWT.
   * NÃO limpa drpera-anon-id de propósito: apagar o anon-id a cada logout
   * regalaria um trial novo de 4 dias por logout (vetor de abuso trial v2).
   */
  const handleLogout = useCallback(() => {
    clearSession();
    clearStoredProfile();
    clearStoredTemplates();
    logout();
  }, [clearSession, logout]);

  /**
   * Wrapper de envio de mensagem.
   * Quando SOAP ON + plantão ativo: registra a mensagem do médico como caso
   * (resumo = primeiras 80 chars). Espelha o pattern da extensão pra backend
   * gerar passagem de plantão útil ao fechar.
   */
  const handleSendMessage = useCallback(
    async (
      content: string,
      attachments?: Parameters<typeof sendUserMessage>[1],
      // Campos aditivos (docRequest da barra "Gerar documento") — passthrough
      options?: Parameters<typeof sendUserMessage>[2],
    ) => {
      if (plantao.ativo && soapMode && content.trim()) {
        plantao.adicionarCaso(content);
      }
      await sendUserMessage(content, attachments, options);
      // Trial v2: atualiza day/casesCount após cada chat (spec: refresh
      // tras cada chat). GET /api/trial/state não inicia o relógio.
      if (trial.flagOn) void trial.refresh();
    },
    [plantao, soapMode, sendUserMessage, trial],
  );

  /**
   * Primeiro anexo do médico (marker localStorage no ChatView) → mensagem
   * local do Dr. Pera explicando o fluxo de exames por foto. kind='local':
   * nunca vai pro LLM, só render na UI.
   */
  const handleFirstAttachment = useCallback(() => {
    appendLocalMessage(t('chat.firstAttachNudge'));
  }, [appendLocalMessage, t]);

  /* ── Trial v2: email in-chat (dia 2-3 anônimo) ──
     Email/código JAMAIS passam por /api/ai/chat — só /api/auth/*. */
  const handleTrialRequestCode = useCallback(
    async (email: string) => {
      await requestMagicCode(email, locale);
    },
    [requestMagicCode, locale],
  );

  const handleTrialVerifyCode = useCallback(
    async (email: string, code: string) => {
      // anonId no verify-code migra relógio + casos + memória server-side
      await login(email, code, trial.anonId);
      appendLocalMessage(t('trial.email.success'));
      clearError();
      void trial.refresh();
    },
    [login, trial, appendLocalMessage, t, clearError],
  );

  /* ── Trial v2: reforço do dia 3 (logado com trial ativo) ──
     UMA VEZ por dia (marker em localStorage): mensagem local do Dr. Pera
     com os números do médico + ponte pra extensão CWS. Roda só com flag
     ligada + ≥1 caso corrido (senão o framing "já corremos N casos" soa
     vazio). Template i18n — nunca vai pro LLM (kind='local'). */
  useEffect(() => {
    if (!trial.flagOn || !user || loadingI18n) return;
    if (!trial.active || trial.day !== 3 || trial.casesCount < 1) return;

    const hoje = new Date().toISOString().slice(0, 10);
    const MARKER_KEY = 'drpera-trial-day3-nudge';
    try {
      if (localStorage.getItem(MARKER_KEY) === hoje) return;
      localStorage.setItem(MARKER_KEY, hoje);
    } catch {
      return; // localStorage indisponível — melhor não repetir a mensagem
    }
    appendLocalMessage(t('trial.day3.nudge', { casesCount: trial.casesCount }));
  }, [
    trial.flagOn,
    trial.active,
    trial.day,
    trial.casesCount,
    user,
    loadingI18n,
    appendLocalMessage,
    t,
  ]);

  /* ── Loading inicial ──
     Trial v2: pra anônimo (fora de /login) espera também a 1ª consulta a
     /api/trial/state (timeout 4s no fetch) — evita flash LoginView → chat
     quando a flag está ligada. Flag off (404) resolve rápido e cai no
     LoginView atual. Logados NUNCA esperam o trial. */
  if (
    authLoading ||
    loadingI18n ||
    (!user && !forceLoginPath && trial.loading)
  ) {
    return <BootScreen />;
  }

  /* ── Anônimo + flag trial_v2 ligada → entra DIRETO no chat ── */
  if ((!user || !jwt) && anonymousTrialMode) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--ink)',
        }}
      >
        <Header
          locale={locale}
          onLocaleChange={setLocale}
          onLogout={handleLogout}
          creditsInfo={null}
          t={t}
          showUserControls={false}
          // Fix 02/07/2026 (Dr. Lu): link "Já sou assinante? Entrar" SEMPRE
          // visível pro anônimo + botão VOZ quando trial ativo (server decide).
          showTrialLogin={true}
          showTrialVoice={trial.active && !trial.trialEnded}
          voiceActive={voiceActive}
          voiceState={voice.state}
          onVoiceClick={handleVoiceClick}
        />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ChatView
            messages={messages}
            loading={chatLoading}
            error={chatError}
            creditsInfo={null}
            soapMode={soapMode}
            plantaoContext={null}
            onSend={handleSendMessage}
            onClearSession={clearSession}
            onClearError={clearError}
            t={t}
            onFirstAttachment={handleFirstAttachment}
            trialV2={{
              // Portas: só anônimo em dia 0-1, sem email pendente nem paywall
              doorsVisible:
                !trial.emailRequired && !trial.trialEnded && trial.day <= 1,
              // Gate de email: dia 2-3 (estado no load OU 403 do chat)
              emailGate: {
                active:
                  !trial.trialEnded &&
                  (trial.emailRequired || chatError?.type === 'email_required'),
                onRequestCode: handleTrialRequestCode,
                onVerifyCode: handleTrialVerifyCode,
                // Locale pro chip "Continuar com Google" (viaja no /start)
                locale,
              },
              // Dia 4+ anônimo: paywall proativo (footer já tem
              // "Já sou assinante? Entrar" → /login, re-entrada atual).
              // reopenNonce: voice pair-init 403 trial_ended reabre o paywall.
              trialEnded: {
                active: trial.trialEnded,
                casesCount: trial.casesCount,
                reopenNonce: trialPaywallReopenNonce,
              },
            }}
          />
        </div>

        {/* Voice trial v2 (02/07/2026) — disclaimer (1ª vez) + pairing QR
            pro anônimo com trial ativo. Upgrade modal NÃO existe aqui:
            rejeições viram paywall trial / email gate via effect. */}
        {voiceModalState === 'disclaimer' && (
          <VoiceDisclaimerModal
            onAccept={handleVoiceDisclaimerAccept}
            onCancel={() => setVoiceModalState('closed')}
            t={t}
          />
        )}
        {voiceModalState === 'pairing' && (
          <VoicePairingModal
            state={voice.state}
            qrUrl={voice.qrUrl}
            lastTranscript={voice.lastTranscript}
            expiresAt={voice.expiresAt}
            errorMessage={voice.error}
            onClose={handleVoicePairingClose}
            onCancel={handleVoicePairingCancel}
            t={t}
          />
        )}

        <Analytics />
        <GoogleAnalytics />
        <CookieBanner t={t} locale={locale} />
      </div>
    );
  }

  /* ── Usuário NÃO autenticado → LoginView (flag off ou /login) ── */
  if (!user || !jwt) {
    return (
      <>
        <Analytics />
        <GoogleAnalytics />
        <LoginView
          onLogin={login}
          onRequestCode={(email, loc) => requestMagicCode(email, loc as Locale)}
          locale={locale}
          onLocaleChange={setLocale}
          t={t}
          initialError={
            googleAuthErrorCode !== null
              ? t(googleAuthErrorToKey(googleAuthErrorCode))
              : undefined
          }
        />
        <CookieBanner t={t} locale={locale} />
      </>
    );
  }

  /* ── Guard legal — autenticado mas ainda não aceitou termos versão atual ── */
  if (!legal.accepted) {
    return (
      <>
        <Analytics />
        <GoogleAnalytics />
        <LegalTermsGate
          t={t}
          onAccept={legal.accept}
          onDecline={handleLogout}
        />
        <CookieBanner t={t} locale={locale} />
      </>
    );
  }

  /* ── Usuário autenticado → ChatView com Header e modais ── */
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--ink)',
      }}
    >
      <Header
        locale={locale}
        onLocaleChange={setLocale}
        onLogout={handleLogout}
        creditsInfo={creditsInfo}
        t={t}
        showUserControls={true}
        soapMode={soapMode}
        onToggleSoap={handleToggleSoap}
        onOpenProfile={() => setProfileOpen(true)}
        onOpenTemplates={() => setTemplatesOpen(true)}
        plantaoAtivo={plantao.ativo}
        plantaoCountdown={plantaoCountdown}
        onTogglePlantao={handleTogglePlantao}
        tier={user?.tier ?? null}
        voiceActive={voiceActive}
        voiceState={voice.state}
        onVoiceClick={handleVoiceClick}
      />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ChatView
          messages={messages}
          loading={chatLoading}
          error={chatError}
          creditsInfo={creditsInfo}
          soapMode={soapMode}
          plantaoContext={plantao.ativo ? plantao.contexto : null}
          onSend={handleSendMessage}
          onClearSession={clearSession}
          onClearError={clearError}
          t={t}
          onFirstAttachment={handleFirstAttachment}
          trialV2={
            // Flag off → null: comportamento atual EXATO pra logados.
            // Flag on: só o fim de trial v2 (dia 4+, HTTP 402 trial_ended
            // ou estado no load) — portas/email não existem pra logados.
            trial.flagOn
              ? {
                  doorsVisible: false,
                  trialEnded: {
                    active: trial.trialEnded,
                    casesCount: trial.casesCount,
                    // Voice 403 trial_ended reabre o paywall (02/07/2026)
                    reopenNonce: trialPaywallReopenNonce,
                  },
                }
              : null
          }
        />
      </div>

      {/* Onboarding tour cinemático (primeira vez do médico, chaveado por userId) */}
      <OnboardingTour
        t={t}
        locale={locale}
        userId={user?.id}
        onOpenProfile={() => {
          setProfileModalClosedForTour(false);
          setProfileOpen(true);
        }}
        onCloseProfile={() => setProfileOpen(false)}
        profileModalClosed={profileModalClosedForTour}
      />

      {/* Modal: Perfil da IA */}
      {profileOpen && (
        <ProfileModal
          onClose={() => {
            setProfileOpen(false);
            setProfileModalClosedForTour(true);
          }}
          t={t}
        />
      )}

      {/* Modal: Templates de documentos */}
      {templatesOpen && (
        <TemplatesModal
          onClose={() => setTemplatesOpen(false)}
          t={t}
        />
      )}

      {/* Modal: Fechar plantão + passagem */}
      {plantaoCloseOpen && (
        <PlantaoCloseModal
          sessionId={sessionId}
          plantaoId={plantao.plantaoId}
          inicio={plantao.inicio}
          casos={plantao.casos}
          consultasIa={plantao.consultasIa}
          creditosUsados={plantao.creditosUsados}
          onConfirmFechar={handleConfirmFecharPlantao}
          t={t}
        />
      )}

      {/* Voice S2 — Disclaimer modal (1ª vez do médico especialista/unlimited) */}
      {voiceModalState === 'disclaimer' && (
        <VoiceDisclaimerModal
          onAccept={handleVoiceDisclaimerAccept}
          onCancel={() => setVoiceModalState('closed')}
          t={t}
        />
      )}

      {/* Voice S2 — Pairing modal com QR code (estado SSE ativo) */}
      {voiceModalState === 'pairing' && (
        <VoicePairingModal
          state={voice.state}
          qrUrl={voice.qrUrl}
          lastTranscript={voice.lastTranscript}
          expiresAt={voice.expiresAt}
          errorMessage={voice.error}
          onClose={handleVoicePairingClose}
          onCancel={handleVoicePairingCancel}
          t={t}
        />
      )}

      {/* Voice S2 — Upgrade modal pra tier free/plantonista */}
      {voiceModalState === 'upgrade' &&
        (user?.tier === 'free' || user?.tier === 'plantonista') && (
          <VoiceUpgradeModal
            tier={user.tier}
            onClose={() => setVoiceModalState('closed')}
            onUpgrade={handleVoiceUpgrade}
            t={t}
          />
        )}

      {/* Analytics: CF Web Analytics + Microsoft Clarity (sempre, sem consent) */}
      <Analytics />

      {/* Google Analytics 4: condicional, apenas após consent */}
      <GoogleAnalytics />

      {/* Banner de consent LGPD/GDPR (5 idiomas) */}
      <CookieBanner t={t} locale={locale} />
    </div>
  );
}
